import styled, { css } from 'styled-components';
import completedProgressMarker from '../../../assets/delivery-progress-completed.svg';
import activeProgressMarker from '../../../assets/delivery-progress-active.svg';
import {
  FontSizes,
  HeaderSecondary,
  HeaderTertiary,
  TextBody as TypographyTextBody,
} from '../../../common/typography/typography.styles';
import { TextBody } from '../../../routes/parcel-list/parcel-list.styles';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { BaseMarginComponent } from '../../../common/base-margin/base-margin.component';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
  ServicesSubSectionNote,
  ServicesSubSectionRow,
  ServicesSubSectionTitle,
  ServicesSubSectionValue,
} from '../../services/services-subsection/services-subsection.styles';

export const Body = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const SectionsList = styled.div`
  display: grid;
  gap: 12px;
  flex: 1;
  align-content: start;
  overflow-y: auto;
  align-items: start;

  &::-webkit-scrollbar {
    width: 14px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.baseColors.grey5};
    border: 4px solid ${({ theme }) => theme.colors.baseColors.blue0};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.baseColors.grey80};
    border: 4px solid ${({ theme }) => theme.colors.baseColors.grey5};
    border-radius: 12px;
  }
`;

export const Section = styled(ServicesSubSectionContainer)`
  min-width: 0;
  &.delivery-status-panel {
    padding: 12px 20px 20px;
    .full-history {
      display: flex;
      justify-content: center;
    }
  }
`;

export const SectionTitle = styled(ServicesSubSectionHeader)``;

interface TitleNoteProps {
  $color: string;
}

export const TitleNote = styled.span<TitleNoteProps>`
  color: ${({ $color }) => $color};
  font-weight: 400;
  font-size: ${FontSizes.normal};
`;

export const Date = styled(TextBody).attrs({ as: 'time' })``;

export const SubSections = styled.div``;

export const SubSectionList = styled.div`
  display: grid;
  gap: 8px;

  ${TypographyTextBody} {
    color: #2c363f;
  }
`;

export const SubSection = styled.div`
  &:not(:last-child) {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #dcdde0;
  }
`;

export const SectionFields = styled.div`
  display: grid;
  gap: 8px;
`;

const rowCommonStyles = css`
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 12px;
`;

export const Title = styled(ServicesSubSectionTitle)`
  top: 0;
`;

export const Value = styled(ServicesSubSectionValue)``;

export const SubjectAddressValue = styled.p`
  font-size: ${FontSizes.medium};
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.5px;
`;

interface RowProps {
  $isFinal?: boolean;
  $isDelivery?: boolean;
  $justifySpaceBetween?: boolean;
}

export const Row = styled(ServicesSubSectionRow)<RowProps>`
  --services-subsection-row-title: 200px;
  grid-template-columns: minmax(100px, 0.62fr) minmax(0, 1fr);
  gap: 12px;
  ${({ $isDelivery }) =>
    $isDelivery &&
    css`
      display: block;
    `}
  align-items: start;
  justify-content: initial;

  ${({ $isFinal, theme }) =>
    $isFinal &&
    css`
      font-size: ${FontSizes.normal};
      line-height: 20px;
      border-top: 1px solid ${theme.colors.baseColors.grey50};
      padding-top: 1.3rem;
      margin-top: -8px;

      @media screen and (max-width: 650px) {
        padding-top: 1.6rem;
        margin-top: -6px;
      }

      ${Value} {
        font-size: ${FontSizes.medium};
        font-weight: 700;
        color: ${theme.colors.baseColors.grey150};
      }
    `}

  ${({ $justifySpaceBetween }) =>
    $justifySpaceBetween &&
    css`
      display: flex;
      justify-content: space-between;
      ${Value} {
        width: auto;
        word-break: keep-all;
        text-align: right;
        font-size: ${FontSizes.small};
        font-weight: 700;
      }
    `}
`;

export const SubSectionRow = styled.div`
  display: grid;
  ${rowCommonStyles};
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  > :last-child {
    text-align: right;
    overflow-wrap: anywhere;
  }

  & > * {
    min-width: 0;
  }
`;

export const ValueContent = styled(HeaderSecondary).attrs({
  as: 'p',
  $size: 'medium',
})`
  display: grid;
  gap: 4px;
  font-weight: 500;
  word-break: break-word;
`;

export const ValueNote = styled(ServicesSubSectionNote).attrs({ as: 'span' })`
  font-size: 1.2rem;
`;

export const DeliveryDetailsContainer = styled.div``;

type DeliveryDetailsFlexContainerProps = {
  $mb: string;
};
export const DeliveryDetailsFlexContainer = styled(
  FlexContainer
)<DeliveryDetailsFlexContainerProps>`
  margin-bottom: ${({ $mb }) => $mb || 0};
  align-items: start;
`;

export const TrackingNumber = styled(BaseMarginComponent('button'))`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.trackingNumber.color};
  cursor: pointer;
  margin-left: 0;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  border: 1px solid ${({ theme }) => theme.colors.baseColors.grey25};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  overflow-wrap: anywhere;
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

export const PostName = styled(HeaderTertiary)`
  word-break: break-all;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  font-weight: 500;
  font-size: ${FontSizes.medium};
  position: relative;
  top: 2px;
`;

export const PostImage = styled.img`
  width: 54px;
  height: 22px;
  flex-shrink: 0;
  object-fit: contain;
`;

export const DeliveryDetailsHeader = styled(FlexContainer)`
  margin-bottom: 8px;
`;

export const ParcelContentTitle = styled(ValueContent)`
  margin-bottom: 4px;
`;

export const DeliveryStatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
  > :first-child {
    min-width: 0;
  }
  > :last-child:not(:first-child) {
    flex-shrink: 0;
  }
`;

export const DeliveryStatusMore = styled.div`
  position: relative;
  display: flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

interface DeliveryStatusAccordionTriggerProps {
  $isActive?: boolean;
  $statusColor?: string;
}

export const DeliveryStatusAccordionTrigger = styled.button<DeliveryStatusAccordionTriggerProps>`
  background: none;
  border: none;
  font: inherit;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  justify-content: space-between;
  padding: 8px 0 0 0;
  text-align: left;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  .status-labels {
    display: grid;
    gap: 4px;
  }
  .status-caption {
    font-size: ${FontSizes.normal};
    color: ${({ theme }) => theme.colors.baseColors.grey100};
  }
  .status-value {
    font-size: ${FontSizes.medium};
    font-weight: 700;
    color: ${({ $statusColor, theme }) =>
      ({
        red: theme.colors.baseColors.red100,
        green: theme.colors.baseColors.green150,
        grey: theme.colors.baseColors.grey100,
        white: theme.colors.baseColors.grey150,
        yellow: theme.colors.baseColors.yellow200,
      })[$statusColor ?? ''] || theme.colors.namedColors.primary};
  }
  &:disabled {
    cursor: default;
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    transition: transform 250ms ease;
    ${({ $isActive }) =>
      $isActive &&
      css`
        transform: rotate(180deg);
      `};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

export const DeliveryStatuses = styled(FlexContainer)`
  margin-top: 4px;
`;

interface DeliveryStatusProps {
  $isActive?: boolean;
}

export const DeliveryStatusItem = styled(FlexContainer)<DeliveryStatusProps>`
  color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.baseColors.grey150
      : theme.colors.baseColors.grey100};
  position: relative;
  padding-left: 28px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    width: 12px;
    height: 12px;
    background: ${({ theme }) => theme.colors.namedColors.primary};
    mask: url(${({ $isActive }) =>
        $isActive ? activeProgressMarker : completedProgressMarker})
      center / 12px 12px no-repeat;
  }
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 18px;
    /* Bridge the 8px row gap, leaving 2px before the next marker box. */
    bottom: -10px;
    width: 2px;
    background: ${({ theme }) => theme.colors.namedColors.primary};
  }
  time {
    text-align: right;
  }
`;

export const ActionButtonContainer = styled.div`
  padding-top: 24px;

  &:empty {
    display: none;
  }
`;

export const IconWrapper = styled.span`
  display: block;
  width: 20px;
  height: 20px;
`;

export const QrcodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  max-width: 320px;

  padding: 12px;

  @media (max-width: 650px) {
    padding-bottom: 0;
    max-width: 264px;
  }
`;

export const CodeStringWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  svg {
    width: 2rem;
    height: 2rem;
  }
  p {
    font-size: ${FontSizes.mediumPlus};
    font-weight: 700;
  }
`;

export const CodeGenerationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  align-items: center;

  margin: 12px -20px 0;
  padding: 12px 20px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.baseColors.grey50};

  & > p {
    font-size: ${FontSizes.normal};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.baseColors.grey150};
  }
`;

export const CodeGenerationButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;

  & > * {
    flex: 1;
  }

  ${CodeStringWrapper} {
    min-width: 0;
    min-height: 48px;
    border: 1px solid ${({ theme }) => theme.colors.baseColors.grey25};
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.namedColors.white};
    padding: 12px;
    color: ${({ theme }) => theme.colors.baseColors.grey150};
    svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
    p {
      font-size: ${FontSizes.medium};
    }
  }

  @media (max-width: 360px) {
    flex-direction: column;
  }
`;

export const BarcodeWrapper = styled.div`
  & > svg {
    text {
      font: 14px ${({ theme }) => theme.fonts.body.fontFamily} !important;
      letter-spacing: 10px;
    }
  }

  @media (max-width: 650px) {
    width: auto;
    height: auto;
    transform: rotate(-90deg);
    & > svg {
      width: 100%;
      height: 100%;
      min-height: 300px;
    }
  }
`;

export const TrackingCodeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  text-align: left;
  cursor: pointer;
  h2 {
    overflow-wrap: anywhere;
    font-size: ${FontSizes.mediumBig};
    line-height: 30px;
  }
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.baseColors.grey100};
  }
`;
